// src/pages/Portfolio.jsx
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import Card from '../components/ui/Card';
import Button from '../components/ui/Button';

const projects = [
  {
    id: 1,
    title: 'City General Hospital',
    category: 'Healthcare',
    description: 'A comprehensive hospital management system that streamlined patient care, appointments, billing, and pharmacy operations for a 300-bed hospital.',
    technologies: ['React', 'Node.js', 'MongoDB', 'Express'],
    results: 'Reduced administrative tasks by 40% and improved patient check-in time by 60%.',
    image: '/api/placeholder/600/400',
    featured: true
  },
  {
    id: 2,
    title: 'Grand Resort Hotels',
    category: 'Hospitality',
    description: 'Implemented a complete hotel management solution for a chain of luxury hotels, covering reservations, room management, guest services, and reporting.',
    technologies: ['Angular', 'Python', 'PostgreSQL', 'Django'],
    results: 'Improved booking efficiency by 50% and increased online bookings by 35%.',
    image: '/api/placeholder/600/400',
    featured: true
  },
  {
    id: 3,
    title: 'QuickBill Finance',
    category: 'Finance',
    description: 'Developed a customized billing and invoicing system for a financial services firm with complex pricing structures and multi-currency support.',
    technologies: ['Vue.js', 'Laravel', 'MySQL', 'Redis'],
    results: 'Reduced billing errors by 90% and decreased payment processing time by 70%.',
    image: '/api/placeholder/600/400',
    featured: false
  },
  {
    id: 4,
    title: 'EduTech Academy',
    category: 'Education',
    description: 'Created a school management system for a group of private schools, handling student records, attendance, grading, and parent communication.',
    technologies: ['React', 'Node.js', 'MongoDB', 'Socket.io'],
    results: 'Improved communication between teachers and parents, with 95% parent engagement.',
    image: '/api/placeholder/600/400',
    featured: false
  },
  {
    id: 5,
    title: 'Retail Connect',
    category: 'Retail',
    description: 'Built an inventory management and POS system for a retail chain with multiple locations, including real-time stock updates and sales analytics.',
    technologies: ['React Native', 'Firebase', 'GraphQL', 'Node.js'],
    results: 'Reduced inventory discrepancies by 80% and improved stock forecasting accuracy by 60%.',
    image: '/api/placeholder/600/400',
    featured: true
  },
  {
    id: 6,
    title: 'LogisticsPro',
    category: 'Logistics',
    description: 'Developed a logistics management system for a transportation company, optimizing route planning, tracking, and delivery management.',
    technologies: ['Angular', 'Spring Boot', 'PostgreSQL', 'Kafka'],
    results: 'Improved delivery times by 30% and reduced fuel costs by 25% through optimized routing.',
    image: '/api/placeholder/600/400',
    featured: false
  }
];

const Portfolio = () => {
  const [filter, setFilter] = useState('All');
  const categories = ['All', 'Healthcare', 'Hospitality', 'Finance', 'Education', 'Retail', 'Logistics'];
  
  const filteredProjects = filter === 'All' 
    ? projects 
    : projects.filter(project => project.category === filter);
  
  return (
    <div className="bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Our Portfolio</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Explore some of our successful projects across various industries. We take pride in delivering customized solutions that drive real business results.
          </p>
        </div>
        
        {/* Category Filters */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setFilter(category)}
              className={`px-4 py-2 rounded-full ${
                filter === category
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100'
              } transition-colors duration-300`}
            >
              {category}
            </button>
          ))}
        </div>
        
        {/* Featured Projects Section (only shown when filter is 'All') */}
        {filter === 'All' && (
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-gray-800 mb-6">Featured Projects</h2>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {projects
                .filter(project => project.featured)
                .map((project) => (
                  <Card key={project.id} className="flex flex-col h-full">
                    <div className="relative">
                      {/* <img
                        src={project.image}
                        alt={project.title}
                        className="w-full h-48 object-cover rounded-t-lg"
                      /> */}
                      <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded">
                        {project.category}
                      </span>
                    </div>
                    <div className="flex-grow p-4">
                      <h3 className="text-xl font-bold text-gray-800 mb-2">{project.title}</h3>
                      <p className="text-gray-600 mb-4">{project.description}</p>
                      <div className="mb-4">
                        <h4 className="font-semibold text-gray-700 mb-1">Technologies:</h4>
                        <div className="flex flex-wrap gap-2">
                          {project.technologies.map((tech) => (
                            <span
                              key={tech}
                              className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                            >
                              {tech}
                            </span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <h4 className="font-semibold text-gray-700 mb-1">Results:</h4>
                        <p className="text-gray-600 text-sm">{project.results}</p>
                      </div>
                    </div>
                  </Card>
                ))}
            </div>
          </div>
        )}
        
        {/* All Projects Section */}
        <div>
          <h2 className="text-2xl font-bold text-gray-800 mb-6">
            {filter === 'All' ? 'All Projects' : `${filter} Projects`}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="flex flex-col h-full">
                <div className="relative">
                  {/* <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-48 object-cover rounded-t-lg"
                  /> */}
                  <span className="absolute top-2 right-2 bg-blue-600 text-white text-xs font-semibold px-2 py-1 rounded">
                    {project.category}
                  </span>
                </div>
                <div className="flex-grow p-4">
                  <h3 className="text-xl font-bold text-gray-800 mb-2">{project.title}</h3>
                  <p className="text-gray-600 mb-4">{project.description}</p>
                  <div className="mb-4">
                    <h4 className="font-semibold text-gray-700 mb-1">Technologies:</h4>
                    <div className="flex flex-wrap gap-2">
                      {project.technologies.map((tech) => (
                        <span
                          key={tech}
                          className="bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-700 mb-1">Results:</h4>
                    <p className="text-gray-600 text-sm">{project.results}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
        
        {/* CTA Section */}
        <div className="mt-16 text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Ready to Start Your Project?</h2>
          <p className="text-lg text-gray-600 mb-6 max-w-2xl mx-auto">
            Let's discuss how we can create a customized solution for your business needs.
          </p>
          <Link to="/contact">
            <Button size="lg">Contact Us Today</Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Portfolio;